import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import pacientesService, { Paciente } from '../../services/pacientesService';
import { 
  ArrowLeft as ArrowLeftIcon, 
  FileText as DocumentTextIcon,
  Calendar as CalendarIcon,
  Printer as PrinterIcon,
  Trash2 as TrashIcon,
  Pencil as EditIcon
} from 'lucide-react';

export default function HistorialClinicoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const pacienteId = Number(id);

  const [loading, setLoading] = useState(true);
  const [paciente, setPaciente] = useState<Paciente | null>(null);

  useEffect(() => {
    loadHistorial();
  }, [pacienteId]);

  const loadHistorial = async () => {
    try {
      setLoading(true);
      const data = await pacientesService.getHistorialClinico(pacienteId);
      setPaciente(data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast.error('Error al cargar historial clínico');
      navigate('/pacientes');
    } finally {
      setLoading(false);
    }
  };

  const calcularEdad = (fechaNacimiento: string) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    if (edad < 1) {
      const meses = mes < 0 ? 12 + mes : mes;
      return `${meses} meses`;
    }
    
    return `${edad} años`;
  };

  const formatFecha = (fecha: string) => {
    const date = new Date(fecha);
    const parts = new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const mon = get('month').replace('.', '').toUpperCase();
    return `${get('day')} ${mon} ${get('year')} ${get('hour')}:${get('minute')}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDeleteReceta = async (consultaId: number) => {
    const confirmed = window.confirm('¿Deseas borrar la receta seleccionada?');
    if (!confirmed) {
      return;
    }

    try {
      await pacientesService.deleteConsulta(consultaId);
      toast.success('Receta borrada correctamente');
      setPaciente((prev) => {
        if (!prev) return prev;
        const consultasActualizadas = prev.consultas?.filter((item) => item.id !== consultaId) || [];
        return {
          ...prev,
          consultas: consultasActualizadas,
        };
      });
    } catch (error) {
      console.error('Error al borrar receta:', error);
      toast.error('No se pudo borrar la receta');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!paciente) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 print:gap-3">
      {/* Header - No se imprime */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/pacientes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DocumentTextIcon className="w-8 h-8 text-green-600" />
              Historial Clínico
            </h1>
            <p className="text-gray-600 mt-1">
              Registro completo de consultas del paciente
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/pacientes/${pacienteId}/consulta`)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <CalendarIcon className="w-5 h-5" />
            Nueva Consulta
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <PrinterIcon className="w-5 h-5" />
            Imprimir
          </button>
        </div>
      </div>

      {/* Información del Paciente */}
      <div className="bg-white rounded-lg shadow p-6 print:p-4 print:shadow-none">
        <div className="border-b border-gray-200 pb-3 mb-3 print:pb-2 print:mb-2">
          <h2 className="text-xl font-bold text-gray-900 print:text-lg">{paciente.nombre}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-sm print:gap-2 print:mt-1 print:text-xs">
            <div>
              <span className="text-gray-600">Edad:</span>
              <span className="ml-2 font-medium">{calcularEdad(paciente.fechaNacimiento)}</span>
            </div>
            <div>
              <span className="text-gray-600">Sexo:</span>
              <span className="ml-2 font-medium">{paciente.sexo === 'M' ? 'Masculino' : 'Femenino'}</span>
            </div>
            <div>
              <span className="text-gray-600">Peso:</span>
              <span className="ml-2 font-medium">{paciente.peso ? `${paciente.peso} kg` : '-'}</span>
            </div>
            <div>
              <span className="text-gray-600">Estatura:</span>
              <span className="ml-2 font-medium">{paciente.estatura ? `${paciente.estatura} cm` : '-'}</span>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Tutor:</span>
              <span className="ml-2 font-medium">{paciente.tutor || '-'}</span>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Teléfono:</span>
              <span className="ml-2 font-medium">{paciente.telefono || '-'}</span>
            </div>
            {paciente.direccion && (
              <div className="md:col-span-4">
                <span className="text-gray-600">Dirección:</span>
                <span className="ml-2 font-medium">{paciente.direccion}</span>
              </div>
            )}
          </div>
        </div>

        {/* Resumen */}
        <div className="bg-blue-50 rounded-lg p-3 print:p-2 print:bg-gray-100">
          <p className="text-sm text-gray-700 print:text-xs">
            <span className="font-semibold">Total de consultas:</span> {paciente.consultas?.length || 0}
          </p>
        </div>
      </div>

      {/* Historial de Consultas */}
      <div className="flex flex-col gap-6 print:gap-3 allow-print-breaks print:break-inside-auto">
        {paciente.consultas && paciente.consultas.length > 0 ? (
          paciente.consultas.map((consulta, index) => (
            <div key={consulta.id} className="bg-white rounded-lg shadow overflow-hidden print:border print:border-gray-300 print:shadow-none print:break-inside-auto print:overflow-visible print:mb-3">
              {/* Separador visual entre consultas */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6 py-3 print:bg-blue-600 print:text-white print:px-4 print:py-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold print:text-base">
                      Consulta #{paciente.consultas!.length - index}
                    </h3>
                    <button
                      onClick={() => handleDeleteReceta(consulta.id)}
                      className="print:hidden flex items-center gap-1 px-2.5 py-0.5 bg-red-50 text-red-600 rounded-md text-xs font-medium hover:bg-red-100 transition-colors border border-red-100"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      Borrar Receta
                    </button>
                    <button
                      onClick={() => navigate(`/pacientes/consultas/${consulta.id}/editar`)}
                      className="print:hidden flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 text-amber-600 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors border border-amber-100"
                    >
                      <EditIcon className="w-3.5 h-3.5" />
                      Editar
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm print:text-xs">{formatFecha(consulta.fecha)}</span>
                    <button
                      onClick={() => navigate(`/pacientes/consultas/${consulta.id}/receta`)}
                      className="print:hidden flex items-center gap-1 px-3 py-1 bg-white text-blue-600 rounded text-sm hover:bg-blue-50 transition-colors"
                    >
                      <PrinterIcon className="w-4 h-4" />
                      Receta
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4 print:p-3 print:space-y-2">
                {/* Datos Vitales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-gray-200 print:grid-cols-4 print:gap-2 print:pb-2">
                  {consulta.temperatura && (
                    <div>
                      <span className="text-xs text-gray-600">Temperatura:</span>
                      <p className="font-medium">{consulta.temperatura}°C</p>
                    </div>
                  )}
                  {consulta.peso && (
                    <div>
                      <span className="text-xs text-gray-600">Peso:</span>
                      <p className="font-medium">{consulta.peso} kg</p>
                    </div>
                  )}
                  {consulta.estatura && (
                    <div>
                      <span className="text-xs text-gray-600">Estatura:</span>
                      <p className="font-medium">{consulta.estatura} cm</p>
                    </div>
                  )}
                </div>

                {/* Revisión Física */}
                {consulta.revisionFisica && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 print:text-xs print:mb-1">Revisión Física:</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap print:text-xs">{consulta.revisionFisica}</p>
                  </div>
                )}

                {/* Diagnósticos */}
                {consulta.diagnosticos && consulta.diagnosticos.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 print:text-xs print:mb-1">Diagnósticos:</h4>
                    <ul className="list-disc list-inside space-y-1 print:space-y-0">
                      {consulta.diagnosticos.map((diag) => (
                        <li key={diag.id} className="text-sm text-gray-600 print:text-xs">
                          {diag.diagnostico}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Medicamentos */}
                {consulta.medicamentos && consulta.medicamentos.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 print:text-xs print:mb-1">Medicamentos Recetados:</h4>
                    <div className="space-y-3 print:space-y-2">
                      {consulta.medicamentos.map((med) => (
                        <div key={med.id} className="bg-gray-50 rounded-lg p-3 print:p-2 print:bg-gray-100">
                          <p className="font-medium text-sm text-gray-900 print:text-xs">{med.medicamento}</p>
                          <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-gray-600 print:mt-1 print:text-[10px]">
                            <div>
                              <span className="font-medium">Dosis:</span> {med.dosis}
                            </div>
                            <div>
                              <span className="font-medium">Frecuencia:</span> {med.frecuencia}
                            </div>
                            <div>
                              <span className="font-medium">Duración:</span> {med.duracion}
                            </div>
                          </div>
                          {med.indicaciones && (
                            <p className="mt-2 text-xs text-gray-600 print:mt-1 print:text-[10px]">
                              <span className="font-medium">Indicaciones:</span> {med.indicaciones}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observaciones */}
                {consulta.observaciones && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 print:text-xs print:mb-1">Observaciones:</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap print:text-xs">{consulta.observaciones}</p>
                  </div>
                )}

              </div>

              {/* Separador decorativo */}
              {index < paciente.consultas!.length - 1 && (
                <div className="border-t-2 border-gray-300 mt-4 print:mt-2 print:border-gray-400"></div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No hay consultas registradas para este paciente</p>
            <button
              onClick={() => navigate(`/pacientes/${pacienteId}/consulta`)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Registrar Primera Consulta
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
